import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/admin/Sidebar';
import TopNavbar from '../components/admin/TopNavbar';
import { supabase } from '../config/supabase';
import './AdminOrderDetails.css';

const AdminOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [adminSession, setAdminSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  const statuses = ['New', 'Preparing', 'Ready', 'Delivered', 'Cancelled'];

  useEffect(() => {
    const session = localStorage.getItem('adminSession') || sessionStorage.getItem('adminSession');
    if (!session) {
      navigate('/admin');
      return;
    }
    setAdminSession(JSON.parse(session));
    fetchOrderDetails();
  }, [id, navigate]);

  const fetchOrderDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        // Transform to component format
        const orderItems = typeof data.items === 'string' 
          ? JSON.parse(data.items) 
          : data.items;

        const statusMap = {
          'pending': 'New',
          'confirmed': 'Preparing',
          'preparing': 'Preparing',
          'ready': 'Ready',
          'out_for_delivery': 'Ready',
          'delivered': 'Delivered',
          'cancelled': 'Cancelled'
        };

        const componentStatus = statusMap[data.status.toLowerCase()] || 'New';

        const transformedOrder = {
          id: data.id,
          orderNumber: 1, // Will be calculated if needed
          customer: {
            name: data.customer_name,
            phone: data.customer_phone || 'N/A'
          },
          items: orderItems.map(item => item.name || item),
          itemCount: orderItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
          amount: parseFloat(data.total),
          status: componentStatus,
          createdAt: new Date(data.created_at),
          deliveryAddress: data.delivery_address,
          paymentMethod: data.payment_method === 'cash' ? 'Cash on Delivery' : 'Card Payment',
          specialInstructions: data.delivery_instructions || null,
          subtotal: parseFloat(data.subtotal),
          deliveryFee: parseFloat(data.delivery_fee),
          fullOrderData: data,
          orderItems: orderItems // Keep full item details
        };

        setOrder(transformedOrder);
        setSelectedStatus(componentStatus);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    const statusMap = {
      'New': 'pending',
      'Preparing': 'preparing',
      'Ready': 'ready',
      'Delivered': 'delivered',
      'Cancelled': 'cancelled'
    };

    const dbStatus = statusMap[newStatus] || 'pending';

    try {
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: dbStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      setSelectedStatus(newStatus);
      setOrder(prev => ({ ...prev, status: newStatus }));
      setShowStatusDropdown(false);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status. Please try again.');
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCall = () => {
    if (order) {
      window.location.href = `tel:${order.customer.phone}`;
    }
  };

  const calculateTotal = () => {
    return order ? order.amount : 0;
  };

  if (!adminSession) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (loading) {
    return (
      <div className="admin-order-details-page">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="admin-order-details-main">
          <TopNavbar 
            onMenuClick={() => setSidebarOpen(true)}
            adminName={adminSession.username || adminSession.email}
          />
          <div className="admin-order-details-content">
            <div className="loading-state">Loading order details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="admin-order-details-page">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="admin-order-details-main">
          <TopNavbar 
            onMenuClick={() => setSidebarOpen(true)}
            adminName={adminSession.username || adminSession.email}
          />
          <div className="admin-order-details-content">
            <div className="error-state">
              <h2>Order Not Found</h2>
              <p>We couldn't find the order you're looking for.</p>
              <button onClick={() => navigate('/admin/orders')} className="back-btn">
                Back to Orders
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-order-details-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="admin-order-details-main">
        <TopNavbar 
          onMenuClick={() => setSidebarOpen(true)}
          adminName={adminSession.username || adminSession.email}
        />
        
        <main className="admin-order-details-content">
          <div className="page-header">
            <button className="back-button" onClick={() => navigate('/admin/orders')}>
              ← Back to Orders
            </button>
            <h1 className="page-title">Order Details - #{order.orderNumber || order.id.slice(0, 8).toUpperCase()}</h1>
          </div>

          <div className="order-details-container">
            {/* Customer Info Section */}
            <section className="details-section">
              <label className="section-label">CUSTOMER DETAILS</label>
              <div className="customer-details">
                <div className="detail-name">{order.customer.name}</div>
                <a href={`tel:${order.customer.phone}`} className="detail-phone">
                  📞 {order.customer.phone}
                </a>
              </div>
            </section>

            {/* Items Section */}
            <section className="details-section">
              <label className="section-label">ITEMS ORDERED</label>
              <div className="items-list">
                {order.orderItems && order.orderItems.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-header">
                      <span className="item-name">{item.name || item}</span>
                      <span className="item-price">
                        Rs. {((item.finalPrice || item.price || 0) * (item.quantity || 1)).toFixed(2)}
                      </span>
                    </div>
                    <div className="item-quantity">Quantity: {item.quantity || 1}</div>
                    
                    {/* Display customization details */}
                    {(item.selectedSize || item.selectedSpice || (item.selectedAddons && item.selectedAddons.length > 0) || item.specialInstructions) && (
                      <div className="item-customizations">
                        {item.selectedSize && (
                          <div className="customization-detail">
                            <strong>Size:</strong> {item.selectedSize}
                          </div>
                        )}
                        {item.selectedSpice && (
                          <div className="customization-detail">
                            <strong>Spice Level:</strong> {item.selectedSpice}
                          </div>
                        )}
                        {item.selectedAddons && item.selectedAddons.length > 0 && (
                          <div className="customization-detail">
                            <strong>Add-ons:</strong> {item.selectedAddons.map(addon => addon.name || addon).join(', ')}
                          </div>
                        )}
                        {item.specialInstructions && (
                          <div className="customization-detail">
                            <strong>Special Instructions:</strong> {item.specialInstructions}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {index < order.orderItems.length - 1 && <div className="item-divider" />}
                  </div>
                ))}
              </div>
            </section>

            {/* Delivery Address Section */}
            <section className="details-section">
              <label className="section-label">DELIVERY ADDRESS</label>
              <div className="address-content">
                <p className="address-text">{order.deliveryAddress}</p>
                {order.specialInstructions && (
                  <div className="instructions-box">
                    <strong>Special Instructions:</strong> {order.specialInstructions}
                  </div>
                )}
              </div>
            </section>

            {/* Bill Details Section */}
            <section className="details-section">
              <label className="section-label">BILL DETAILS</label>
              <div className="bill-details">
                <div className="bill-row">
                  <span>Subtotal</span>
                  <span>Rs. {order.subtotal.toFixed(2)}</span>
                </div>
                <div className="bill-row">
                  <span>
                    Delivery Fee
                    {order.deliveryFee === 0 && <span className="free-tag">FREE</span>}
                  </span>
                  <span>Rs. {order.deliveryFee.toFixed(2)}</span>
                </div>
                <div className="bill-divider" />
                <div className="bill-row total-row">
                  <span>Grand Total</span>
                  <span>Rs. {calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            </section>

            {/* Payment & Time Section */}
            <section className="details-section">
              <div className="info-grid">
                <div className="info-item">
                  <label>Payment Method</label>
                  <div className="info-value">
                    💳 {order.paymentMethod}
                  </div>
                </div>
                <div className="info-item">
                  <label>Order Time</label>
                  <div className="info-value">
                    {formatTime(order.createdAt)}, {formatDate(order.createdAt)}
                  </div>
                </div>
                <div className="info-item">
                  <label>Status</label>
                  <div className="info-value">
                    <span className={`status-badge status-${order.status.toLowerCase()}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="order-actions-footer">
            <button className="action-btn print-btn" onClick={handlePrint}>
              🖨️ Print Order
            </button>
            <button className="action-btn call-btn" onClick={handleCall}>
              📞 Call Customer
            </button>
            <div className="status-dropdown-wrapper">
              <button
                className="action-btn status-btn"
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              >
                Update Status
                <span className="dropdown-arrow">▼</span>
              </button>
              {showStatusDropdown && (
                <div className="status-dropdown">
                  {statuses.map(status => (
                    <button
                      key={status}
                      className={`status-option ${selectedStatus === status ? 'active' : ''}`}
                      onClick={() => handleStatusUpdate(status)}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className="action-btn cancel-btn"
              onClick={() => handleStatusUpdate('Cancelled')}
            >
              Cancel Order
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminOrderDetails;

